import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

const Header = ({ toggleMobileMenu, isMobileMenuOpen }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const queryClient = useQueryClient();
  const apiUrl = process.env.REACT_APP_API_URL;

  const getToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token');
  };

  // Fetch current user data
  const { data: currentUser, isLoading: userLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const token = getToken();
      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${apiUrl}/api/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token is invalid, redirect to auth
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          sessionStorage.removeItem('token');
          window.location.href = '/auth';
          return null;
        }
        throw new Error('Failed to fetch user profile');
      }
      
      return response.json();
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const token = getToken();
      
      const response = await fetch(`${apiUrl}/api/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });
      
      if (!response.ok) {
        throw new Error('Logout failed');
      }
      
      return response.json();
    },
    onSuccess: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('token');
      
      queryClient.clear();
      window.location.href = '/auth';
    },
    onError: (error) => {
      console.error('Logout error:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      sessionStorage.removeItem('token');
      window.location.href = '/auth';
    }
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logoutMutation.mutate();
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Function to get initials from name
  const getInitials = (name) => {
    if (!name) return 'WS'; // fallback
    
    const words = name.trim().split(' ');
    if (words.length === 1) {
      // Single word - take first two characters
      return words[0].substring(0, 2).toUpperCase();
    } else {
      // Multiple words - take first character of first two words
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  };

  // Get user display data
  const userName = currentUser?.name || 'المستخدم';
  const userInitials = getInitials(currentUser?.name);
  const avatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(userInitials)}&background=4F46E5&color=fff&rounded=true`;

  return (
    <header className="header-dash">
      <div className="header-content-dash">
        <div className="header-left-dash">
          {/* Mobile menu toggle button */}
          <button 
            className="mobile-menu-toggle"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <svg className="hamburger" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMobileMenuOpen ? (
                // X icon when menu is open
                <>
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </>
              ) : (
                // Hamburger icon when menu is closed
                <>
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </>
              )}
            </svg>
          </button>
          
          <div className="logo-dash">د</div>
          <div className="header-title-dash">
            <h1>الدار العراقية للأخبار</h1>
            <p>لوحة التحكم الإدارية</p>
          </div>
        </div>
        <div className="header-right-dash">
          <div className="user-dropdown" ref={dropdownRef}>
            <div className="user-info" onClick={toggleDropdown}>
              {userLoading ? (
                <div 
                  className="user-avatar"
                  style={{ 
                    backgroundColor: '#f3f4f6', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center' 
                  }}
                >
                  <span style={{ fontSize: '12px', color: '#6b7280' }}>...</span>
                </div>
              ) : (
                <img 
                  src={avatarUrl}
                  alt={userName}
                  className="user-avatar"
                />
              )}
              <span className="user-name">
                {userLoading ? 'جاري التحميل...' : userName}
              </span>
              <svg 
                className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                width="12" 
                height="12" 
                viewBox="0 0 12 12"
                fill="currentColor"
              >
                <path d="M6 8.5L2 4.5h8L6 8.5z"/>
              </svg>
            </div>
            
            {isDropdownOpen && (
              <div className="dropdown-menu">
                {currentUser && (
                  <>
                    <div className="dropdown-item user-profile-info" style={{ 
                      padding: '8px 12px', 
                      borderBottom: '1px solid #e5e7eb',
                      background: '#f9fafb'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>{currentUser.name}</div>
                    </div>
                    <div className="dropdown-divider"></div>
                  </>
                )}
                
                <button 
                  className="dropdown-item logout-item"
                  onClick={handleLogout}
                  disabled={logoutMutation.isPending}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
                    <path fillRule="evenodd" d="M4.146 8.354a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H14.5a.5.5 0 0 1 0 1H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3z"/>
                  </svg>
                  {logoutMutation.isPending ? 'جارِ الخروج...' : 'تسجيل الخروج'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;